import { getDocumentElement, getDocumentElements, getChild } from "../../Utils/Utils";

/**
 * Create the summary section of the calculator.
 * 
 * @param   {object}    options
 * 
 * @returns {object}
 */
const CreateSummary = options => {
    const SummarySection = getDocumentElement(options.selectors.section);
    const TopNavButtons = getDocumentElements(options.selectors.topNavButton);
    const BottomNavButtons = getDocumentElements(options.selectors.bottomNavButton);
    const SummaryAccordion = getChild(SummarySection, '.farm-calc-accordion');

    /**
     * Add updated class to Summary accordion
     */
    const __addUpdatedClass = () => {
        SummaryAccordion.classList.add('updated')
    }

    /**
     * Update the summary fields.
     * 
     * @returns     {void}
     */
    const __updateSummaryFieldsWaterUse = () => {     
        const WaterUseSection = getDocumentElement('.farm-calc-section--use');
        const StorageSection = getDocumentElement('.farm-calc-section--storage');

        // Get all water use summary fields
        const SummaryLivestockRequirement = getChild(SummarySection, '#tot_livestock');
        const SummaryHouseholdWaterUse = getChild(SummarySection, '#tot_resident');
        const SummaryGardenWaterUse = getChild(SummarySection, '#tot_garden');
        const SummaryDairyWaterUse = getChild(SummarySection, '#tot_dairy');
        const SummaryOtherWaterUse = getChild(SummarySection, '#tot_other');
        const SummaryEvapVolume = getChild(SummarySection, '#tot_evaporation');
        const SummarySeepageVolume = getChild(SummarySection, '#tot_seepage');
        const OverallFarmWaterUse = getChild(SummarySection, '#tot_overall');

        // Get related fields from other sections to push into summary fields
        const LivestockTotal = getChild(WaterUseSection, '#livestockTotal');
        const HouseholdTotal = getChild(WaterUseSection, '#residentTotal');
        const GardenTotal = getChild(WaterUseSection, '#gardenTotal');
        const DairyTotal = getChild(WaterUseSection, '#dairyOverallTotal');
        const OtherTotal = getChild(WaterUseSection, '#otherTotal');
        const EvapTotal = getChild(StorageSection, '#evapTotal');
        const SeepageTotal = getChild(StorageSection, '#seepageTotal');
        const FarmTotal = getChild(WaterUseSection, '#onFarmTotal');
        // Update water use summary fields
        const values = {
            livestock: LivestockTotal.valueAsNumber,
            household: HouseholdTotal.valueAsNumber,
            garden: GardenTotal.valueAsNumber,
            dairy: DairyTotal.valueAsNumber,
            other: OtherTotal.valueAsNumber,
            evaporation: EvapTotal.valueAsNumber,
            seepage: SeepageTotal.valueAsNumber,
            overall: FarmTotal.valueAsNumber
        };

        SummaryLivestockRequirement.value = values.livestock;
        SummaryHouseholdWaterUse.value = values.household;
        SummaryGardenWaterUse.value = values.garden;
        SummaryDairyWaterUse.value = values.dairy;
        SummaryOtherWaterUse.value = values.other;
        SummaryEvapVolume.value = values.evaporation;
        SummarySeepageVolume.value = values.seepage;
        OverallFarmWaterUse.value = values.overall;

        // Add updated class if any values are greater than zero
        if (Object.values(values).some(val => val > 0)) {
            __addUpdatedClass();
        }
    };
    
    /**
     * Water supply summary field updates.
     * 
     * @returns     {void}
     */
    const __updateSummaryFieldsWaterSupply = () => {
        const SupplySection = getDocumentElement('.farm-calc-section--supply');
        const StorageSection = getDocumentElement('.farm-calc-section--storage');

        // Get all water supply summary fields
        const SummaryCreeksRiversGroundwater = getChild(SummarySection, '#tot_supply_type');
        const SummaryRoof = getChild(SummarySection, '#tot_roof');
        const SummaryCatchment = getChild(SummarySection, '#tot_catchment');
        const SummaryRainOnDam = getChild(SummarySection, '#tot_dam');
        const SummarySupplyTotal = getChild(SummarySection, '#tot_water_supply');

        // Get related fields from other sections to push into summary fields
        const values = {
            creeksRiversGroundwater: getChild(SupplySection, '#supplyTotal').valueAsNumber,
            roof: getChild(SupplySection, '#roofTotal').valueAsNumber,
            catchment: getChild(SupplySection, '#catchmentTotal').valueAsNumber,
            rainOnDam: getChild(StorageSection, '#rainTotal').valueAsNumber
        };

        // Update water supply summary fields
        SummaryCreeksRiversGroundwater.value = values.creeksRiversGroundwater;
        SummaryRoof.value = values.roof;
        SummaryCatchment.value = values.catchment;
        SummaryRainOnDam.value = values.rainOnDam;

        // Update supply total
        const fallback = (val) => isNaN(val) ? 0 : val;
        const total =
            fallback(values.creeksRiversGroundwater) +
            fallback(values.roof) +
            fallback(values.catchment) +
            fallback(values.rainOnDam);

        SummarySupplyTotal.value = total.toFixed(3);
            
        // Add updated class if any values are greater than zero
        if (Object.values(values).some(val => val > 0)) {
            __addUpdatedClass();
        }
    };

    /**
     * Water supply storage field updates.
     * 
     * @returns     {void}
     */
    const __updateSummaryFieldsWaterStorage = () => {
        const StorageSection = getDocumentElement('.farm-calc-section--storage');

        // Get all water storage summary fields
        const SummaryDam = getChild(SummarySection, '#tot_dam_volume');
        const SummaryTank = getChild(SummarySection, '#tot_tanks_volume');
        const SummaryStorage = getChild(SummarySection, '#tot_water_storage');

        // Get related fields from other sections to push into summary fields
        const values = {
            dam: getChild(StorageSection, '#tot_dam_volume').valueAsNumber,
            tank: getChild(StorageSection, '#tot_tanks_volume').valueAsNumber,
            total: getChild(StorageSection, '#waterStorageTotal').valueAsNumber
        };

        // Update water storage summary fields
        SummaryDam.value = values.dam;
        SummaryTank.value = values.tank;
        SummaryStorage.value = values.total;
        
        // Add updated class if any values are greater than zero
        if (Object.values(values).some(val => val > 0)) {
            __addUpdatedClass();
        }
    };

    /**
     * Bind the event listeners to force an update on the summary fields.
     * 
     * @returns     {void}
     */
    const __bind = () => {
        const WatchElements = [...BottomNavButtons, ...TopNavButtons];

        WatchElements.forEach(el => {
            el.addEventListener('click', () => {
                __updateSummaryFieldsWaterUse();
                __updateSummaryFieldsWaterSupply();
                __updateSummaryFieldsWaterStorage();
            });
        });
    };
    
    /**
     * Init the summary component.
     */
    const init = () => {
        if (SummarySection && TopNavButtons && BottomNavButtons) {
            __bind();
        }
    };

    /**
     * Return the exposed init function.
     */
    return Object.freeze({ init });
};

export default CreateSummary;
